import { useNavigate, useLocation } from "react-router-dom";
import { ArrowLeft, Share2, Heart } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Listing } from "@/lib/types";
import { toast } from "sonner";
import { formatCurrency } from "@/lib/formatters";

interface PropertyViewHeaderProps {
  property: Listing;
  isFavorite: boolean;
  toggleFavorite: () => void;
}

const PropertyViewHeader = ({
  property,
  isFavorite,
  toggleFavorite,
}: PropertyViewHeaderProps) => {
  const navigate = useNavigate();
  const location = useLocation();

  // Format price
  const formattedPrice = formatCurrency(property.price);

  // Format listing type for display
  const listingTypeDisplay =
    property.listing_type === "sale"
      ? "For Sale"
      : property.listing_type === "rent"
      ? "For Rent"
      : "For Lease";

  // Handle share
  const handleShare = () => {
    if (navigator.share) {
      navigator
        .share({
          title: property?.title || "Commercial Property",
          url: window.location.href,
        })
        .catch(() => {
          navigator.clipboard.writeText(window.location.href);
          toast.success("Link copied to clipboard!");
        });
    } else {
      // Fallback - copy to clipboard
      navigator.clipboard.writeText(window.location.href);
      toast.success("Link copied to clipboard!");
    }
  };

  // Handle back navigation dynamically
  const handleBackClick = () => {
    // Check if a 'from' state was passed during navigation
    if (location.state && (location.state as { from?: string }).from) {
      navigate((location.state as { from: string }).from);
    } else if (
      document.referrer &&
      document.referrer.includes(window.location.origin)
    ) {
      // If referrer is from the same origin, go back in history
      navigate(-1);
    } else {
      // Fallback to a default page if no referrer or external referrer
      navigate("/properties");
    }
  };

  // Safely get address text
  const getAddressText = () => {
    if (typeof property.address === "string" && property.address) {
      return property.address;
    }
    // If property.address is not a string or is empty, return "Sri Lanka"
    return "Sri Lanka";
  };

  return (
    <>
      {/* Back Link */}
      <div className="mb-4">
        <Button
          variant="ghost"
          onClick={handleBackClick}
          className="group flex items-center text-propertylk-blue hover:text-propertylk-darkblue transition-colors px-0"
        >
          <ArrowLeft className="w-4 h-4 mr-2 group-hover:translate-x-[-2px] transition-transform" />
          <span className="font-medium">Back</span>
        </Button>
      </div>

      {/* Property Header */}
      <div className="p-4 sm:p-6 border-b border-propertylk-blue/20 bg-white rounded-t-lg shadow-sm mb-6">
        <div className="flex flex-col sm:flex-row justify-between gap-4">
          <div className="flex-grow">
            <div className="flex flex-wrap items-center gap-2 mb-2">
              <Badge className="bg-propertylk-blue hover:bg-propertylk-darkblue text-white">
                {listingTypeDisplay}
              </Badge>
              <span className="text-gray-500 text-sm">
                Ref: {property.cplk_ref || property.listing_code}
              </span>
            </div>

            <h1 className="text-xl sm:text-2xl font-bold text-propertylk-darkblue mb-2">
              {property.title}
            </h1>

            <div className="flex items-center text-gray-600">
              <span>{getAddressText()}</span>
            </div>
          </div>

          <div className="flex flex-col items-start sm:items-end">
            <div className="text-xl sm:text-2xl font-bold text-propertylk-blue mb-2">
              {formattedPrice}
              {property.listing_type === "rent" && (
                <span className="text-sm font-normal text-gray-500">
                  /month
                </span>
              )}
            </div>

            <div className="flex gap-2">
              <Button
                variant="outline"
                size="sm"
                className="flex items-center gap-1.5 border-propertylk-blue/30 hover:border-propertylk-blue hover:bg-propertylk-lightblue"
                onClick={toggleFavorite}
              >
                <Heart
                  className={`w-4 h-4 ${
                    isFavorite ? "fill-rose-500 text-rose-500" : ""
                  }`}
                />
                {isFavorite ? "Saved" : "Save"}
              </Button>

              <Button
                variant="outline"
                size="sm"
                className="flex items-center gap-1.5 border-propertylk-blue/30 hover:border-propertylk-blue hover:bg-propertylk-lightblue"
                onClick={handleShare}
              >
                <Share2 className="w-4 h-4" />
                Share
              </Button>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default PropertyViewHeader;
